/**
 * Iter 20.21.f / Message 765 — single source of truth for the public
 * "Prize" / "Cash alternative" cell value.
 *
 * Hard rule (per user spec): NEVER fall back to raw `prize_value_estimate`
 * or raw `cash_alternative_value` on a public surface. The backend
 * `compute_display_value()` is the only authority. When it says
 * `display_value_amount = null`, the cell renders `"—"` — not £2 from
 * a stale truncated stored value.
 *
 * This module is the ONE place public cards/stats may read a prize
 * value from. Any new public surface must import these helpers.
 *
 * Note: we duplicate the small `formatGBP` helper here (rather than
 * importing from `./api`) so that jest tests can load this file
 * without pulling axios through CRA's default Babel transform.
 */

const formatGBP = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
};

/**
 * Tiny stored values (≤ £10) for a record with no obvious tiny-prize
 * signal in the title are the truncation-bug shape (`£2,000` →
 * `prize_value_estimate=2`). Frontend treats them as unknown.
 */
const TINY_PRIZE_SIGNALS = [
  "voucher", "gift card", "gift-card", "spin", "token",
  "credit", "free entry", "freebie", "scratch",
];

const hasTinyPrizeSignal = (title) =>
  TINY_PRIZE_SIGNALS.some((s) => (title || "").toLowerCase().includes(s));

/**
 * Public prize cell. Reads ONLY the backend display contract — never
 * falls back to `prize_value_estimate`.
 *
 * Returns: { label, value, source }
 *   label : human-readable label e.g. "Prize value" / "Advertised cash alternative"
 *   value : formatted GBP string or "—" when unknown
 *   source: "title" | "stored" | "unknown"
 */
export function publicPrizeCell(comp) {
  if (!comp) return { label: "Prize value", value: "—", source: "unknown" };
  const amount = comp.display_value_amount;
  const label = comp.display_value_label || "Prize value";
  const source = comp.display_value_source || "unknown";
  if (amount == null) {
    return { label, value: "—", source };
  }
  // Defence-in-depth — even if the backend payload sneaks through a
  // tiny truncated value for a non-tiny-prize title, frontend refuses
  // to render it.
  if (amount <= 10 && !hasTinyPrizeSignal(comp.title)) {
    return { label, value: "—", source: "unknown" };
  }
  return { label, value: formatGBP(amount), source };
}

/**
 * Public cash-alternative cell. We honour only explicit advertised cash
 * alternatives from the backend contract. Included/title cash is exposed
 * separately through `publicIncludedCashCell`.
 *
 * Returns: { label, value }
 */
export function publicCashAltCell(comp) {
  if (!comp) return { label: "Cash alternative", value: "—", present: false };
  const advertised = comp.advertised_cash_alternative;
  if (advertised != null && advertised > 10) {
    return {
      label: "Cash alternative",
      value: formatGBP(advertised),
      present: true,
    };
  }
  return { label: "Cash alternative", value: "—", present: false };
}

export function publicIncludedCashCell(comp) {
  if (!comp) return { label: "Included cash", value: "—", present: false };
  const included = comp.included_cash_amount ?? comp.bonus_cash_amount;
  if (included != null && included > 10) {
    return {
      label: "Included cash",
      value: formatGBP(included),
      present: true,
    };
  }
  return { label: "Included cash", value: "—", present: false };
}

/**
 * Filter the backend-generated `s.explanations` array to hide any line
 * that quotes a prize-worth figure when the public display value is
 * unknown. Prevents copy like "Each ticket costs £1.99 for a prize
 * worth ~£2" from reaching a public detail page.
 */
export function filterExplanationsForUnknownPrize(explanations, comp) {
  if (!Array.isArray(explanations) || !comp) return explanations || [];
  // If the display value is known, no filtering needed.
  if (comp.display_value_amount != null) return explanations;
  // Otherwise drop any line that talks about prize worth / prize value
  // in money terms.
  return explanations.filter((e) => {
    const s = String(e || "").toLowerCase();
    if (!s.includes("£")) return true;
    if (s.includes("prize worth")) return false;
    if (s.includes("prize value")) return false;
    if (s.includes("for a prize")) return false;
    return true;
  });
}
